/* Dainty Forces customizer engine. Replaces Kickflip.
   One reusable component across all customizable necklaces.
   - Color selector switches the disc base (gold/silver).
   - Disc 1 is required and included in the base price.
   - Discs 2-4 are optional; each added disc moves to the next price tier.
   - Live preview renders each chosen icon on its disc.
   - On every change it writes the correct variant id + line item
     properties into the product form so the order shows every pick. */

(function () {
  class DaintyCustomizer extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.formId = this.dataset.formId;
      this.form = document.getElementById(this.formId);
      this.config = this._json('[data-dainty-config]') || {};
      this.variants = this._json('[data-dainty-variants]') || [];

      // state
      this.color = null;          // { label, disc, metal }
      this.discCount = 1;         // 1..maxDiscs
      // maxDiscs: how many discs this product allows.
      // Birth flower NECKLACE = 4 (build-your-own); single add-on CHARM = 1.
      this.maxDiscs = parseInt(this.config.max_discs, 10) || 4;
      this.picks = {};            // { 1: {label,img}, 2: {...} }
      this.inputsHost = this.querySelector('[data-dainty-inputs]');

      // single-disc products: no tabs, no add/remove, just one picker
      if (this.maxDiscs <= 1) this.classList.add('dainty--single');

      // hide the little text label under each bubble when the icon already
      // shows the choice (e.g. the Initials necklace, where the letter is the icon)
      if (this.config.hide_labels) this.classList.add('dainty--no-labels');

      // fixed-layout mode (e.g. Sweetheart necklace): a set number of slots,
      // some are pickers and some are preset (a locked charm like the heart).
      // Driven entirely by config.slots, so standard products are untouched.
      this.fixed = Array.isArray(this.config.slots) && this.config.slots.length > 0;
      this.slots = this.fixed ? this.config.slots : null;
      if (this.fixed) {
        this.classList.add('dainty--fixed');
        this.discCount = this.slots.length;
        // seed the preset slots so their charm shows from the start
        this.slots.forEach((s, idx) => {
          if (s.type === 'preset') {
            this.picks[idx + 1] = { label: s.value || s.label, img: s.img, preset: true };
          }
        });
      }

      this._bindColors();
      this._bindTabs();
      this._bindBubbles();
      this._bindAddRemove();

      // defaults: first color selected, disc 1 active
      const firstColor = this.querySelector('.dainty-swatch.is-selected');
      if (firstColor) this._selectColor(firstColor);

      this._sync();
    }

    _json(sel) {
      const el = this.querySelector(sel);
      if (!el) return null;
      try { return JSON.parse(el.textContent); } catch (e) { return null; }
    }

    /* ---------- color ---------- */
    _bindColors() {
      this.querySelectorAll('[data-dainty-color]').forEach((btn) => {
        btn.addEventListener('click', () => this._selectColor(btn));
      });
    }
    _selectColor(btn) {
      this.querySelectorAll('.dainty-swatch').forEach((b) => {
        b.classList.toggle('is-selected', b === btn);
        b.setAttribute('aria-checked', b === btn ? 'true' : 'false');
      });
      this.color = {
        label: btn.dataset.daintyColor,
        disc: btn.dataset.daintyDisc || '',
        metal: btn.dataset.daintyMetal || 'gold'
      };
      const valueEl = this.querySelector('[data-dainty-color-value]');
      if (valueEl) valueEl.textContent = this.color.label;
      // switch the preview metal (gold/silver) via a class the CSS styles
      this.classList.remove('dainty--metal-gold', 'dainty--metal-silver');
      this.classList.add(`dainty--metal-${this.color.metal}`);
      // if the swatch supplies an actual disc image, use it; else CSS draws the metal
      this.querySelectorAll('.dainty-preview__base').forEach((b) => {
        b.style.backgroundImage = this.color.disc ? `url(${this.color.disc})` : '';
      });
      this._sync();
    }

    /* ---------- tabs ---------- */
    _bindTabs() {
      this.querySelectorAll('[data-dainty-tab]').forEach((tab) => {
        tab.addEventListener('click', () => this._showDisc(parseInt(tab.dataset.daintyTab, 10)));
      });
    }
    _showDisc(n) {
      this.querySelectorAll('[data-dainty-tab]').forEach((t) => {
        const active = parseInt(t.dataset.daintyTab, 10) === n;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      this.querySelectorAll('[data-dainty-panel]').forEach((p) => {
        p.classList.toggle('is-hidden', parseInt(p.dataset.daintyPanel, 10) !== n);
      });
    }

    /* ---------- bubbles ---------- */
    _bindBubbles() {
      this.querySelectorAll('[data-dainty-panel]').forEach((panel) => {
        const disc = parseInt(panel.dataset.daintyPanel, 10);
        panel.querySelectorAll('[data-dainty-option]').forEach((bubble) => {
          bubble.addEventListener('click', () => {
            panel.querySelectorAll('.dainty-bubble').forEach((b) => {
              b.classList.toggle('is-selected', b === bubble);
              b.setAttribute('aria-checked', b === bubble ? 'true' : 'false');
            });
            this.picks[disc] = { label: bubble.dataset.daintyOption, img: bubble.dataset.daintyImg };
            this._renderPreviewDisc(disc);
            this._sync();
          });
        });
      });
    }

    /* ---------- add / remove discs ---------- */
    _bindAddRemove() {
      const addBtn = this.querySelector('[data-dainty-add]');
      if (addBtn) addBtn.addEventListener('click', () => this._addDisc());
      this.querySelectorAll('[data-dainty-remove]').forEach((btn) => {
        btn.addEventListener('click', () => this._removeDisc(parseInt(btn.dataset.daintyRemove, 10)));
      });
    }
    _addDisc() {
      if (this.fixed) return;
      if (this.discCount >= this.maxDiscs) return;
      this.discCount += 1;
      const n = this.discCount;
      const tab = this.querySelector(`[data-dainty-tab="${n}"]`);
      if (tab) tab.classList.remove('is-hidden');
      if (this.discCount >= this.maxDiscs) {
        const add = this.querySelector('[data-dainty-add]');
        if (add) add.classList.add('is-hidden');
      }
      this._showDisc(n);
      this._sync();
    }
    _removeDisc(n) {
      if (this.fixed) return;
      // drop this disc, shift higher discs down so numbering stays 1..count
      for (let i = n; i < this.discCount; i++) {
        this.picks[i] = this.picks[i + 1];
        this._applyPanelSelection(i);
        this._renderPreviewDisc(i);
      }
      delete this.picks[this.discCount];
      this._clearPanelSelection(this.discCount);
      this._renderPreviewDisc(this.discCount);
      const lastTab = this.querySelector(`[data-dainty-tab="${this.discCount}"]`);
      if (lastTab) lastTab.classList.add('is-hidden');
      this.discCount -= 1;
      const add = this.querySelector('[data-dainty-add]');
      if (add) add.classList.remove('is-hidden');
      this._showDisc(Math.min(n, this.discCount));
      this._sync();
    }
    _applyPanelSelection(disc) {
      const panel = this.querySelector(`[data-dainty-panel="${disc}"]`);
      if (!panel) return;
      const pick = this.picks[disc];
      panel.querySelectorAll('.dainty-bubble').forEach((b) => {
        const match = pick && b.dataset.daintyOption === pick.label;
        b.classList.toggle('is-selected', !!match);
        b.setAttribute('aria-checked', match ? 'true' : 'false');
      });
    }
    _clearPanelSelection(disc) {
      const panel = this.querySelector(`[data-dainty-panel="${disc}"]`);
      if (!panel) return;
      panel.querySelectorAll('.dainty-bubble').forEach((b) => {
        b.classList.remove('is-selected');
        b.setAttribute('aria-checked', 'false');
      });
    }

    /* ---------- preview ---------- */
    _renderPreviewDisc(disc) {
      const icon = this.querySelector(`[data-dainty-preview-icon="${disc}"]`);
      if (!icon) return;
      const pick = this.picks[disc];
      if (pick && disc <= this.discCount) {
        icon.src = pick.img;
        icon.alt = pick.label;
        icon.hidden = false;
      } else {
        icon.hidden = true;
        icon.removeAttribute('src');
      }
    }
    _renderAllPreview() {
      for (let i = 1; i <= 4; i++) {
        const discEl = this.querySelector(`[data-dainty-preview-disc="${i}"]`);
        if (discEl) discEl.classList.toggle('is-hidden', i > this.discCount);
        this._renderPreviewDisc(i);
      }
    }

    /* ---------- variant resolution + form sync ---------- */
    _matchVariant() {
      // option1 holds the disc-count tier value, e.g. "1 Disc", "2 Discs".
      // Match by the leading number so wording can vary safely.
      const want = this.discCount;
      let match = this.variants.find((v) => {
        const m = String(v.option1 || '').match(/\d+/);
        return m && parseInt(m[0], 10) === want;
      });
      // fallback: nth variant by order
      if (!match && this.variants[want - 1]) match = this.variants[want - 1];
      return match || this.variants[0];
    }

    _sync() {
      this._renderAllPreview();

      const variant = this._matchVariant();

      // 1) set the variant id on the theme's own input so price + cart are correct
      if (variant && this.form) {
        const idInput = this.form.querySelector('input[name="id"], .product-variant-id');
        if (idInput) {
          idInput.value = variant.id;
          idInput.disabled = false;
        }
      }

      // 2) rebuild line item property inputs
      if (this.inputsHost) {
        this.inputsHost.innerHTML = '';
        if (this.color) {
          this.inputsHost.appendChild(this._prop(this.config.color_label || 'Color', this.color.label));
        }
        const discLabel = this.config.disc_label || 'Disc';
        for (let i = 1; i <= this.discCount; i++) {
          const pick = this.picks[i];
          // single-charm products read cleanly "Birth Flower: May" (no number);
          // multi-disc necklaces number each disc "Disc 1 / Disc 2 ..."
          const name = this.maxDiscs <= 1 ? discLabel : `${discLabel} ${i}`;
          this.inputsHost.appendChild(this._prop(name, pick ? pick.label : 'None'));
        }
      }

      // 3) update the on-page price display (theme's price element)
      this._updatePriceDisplay(variant);

      // 4) gate add-to-cart until disc 1 is chosen
      this._updateSubmitState();
    }

    _prop(name, value) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${name}]`;
      input.value = value;
      input.setAttribute('form', this.formId);
      return input;
    }

    _updatePriceDisplay(variant) {
      if (!variant) return;
      const sectionId = this.dataset.sectionId;
      const priceWrap = document.getElementById(`price-${sectionId}`);
      if (!priceWrap) return;
      const money = this._money(variant.price);
      priceWrap.querySelectorAll('.price-item--regular, .price-item--sale').forEach((el) => {
        el.textContent = money;
      });
    }

    _money(cents) {
      const amount = (cents / 100).toFixed(2);
      // Shopify money format; USD store. Keep simple + correct for this store.
      return `$${amount}`;
    }

    _updateSubmitState() {
      const ready = !!this.picks[1];
      if (!this.form) return;
      const btn = this.form.querySelector('[name="add"]');
      if (btn) {
        btn.disabled = !ready;
        btn.classList.toggle('dainty-disabled', !ready);
      }
    }
  }

  if (!customElements.get('dainty-customizer')) {
    customElements.define('dainty-customizer', DaintyCustomizer);
  }
})();